// ⚠️  GENERATED — do not edit. Run `npm run inferrex:generate` to regenerate.

import { useEffect, useState, type FormEvent } from 'react';
import { useAuth, ACTIVE_AUTH_PROVIDER } from './index.js';
import { looksLikeTenantId, setPendingTenantEntry, WORKSPACE_SIGN_IN_PATH } from '../pending-tenant-entry.js';

/**
 * The workspace-ID box (tenant-scoped sign-in, 2026-10-10). Naming a workspace here remembers it
 * for after sign-in; the app then asks the API to record the entry. The ID is a selector, not a
 * password — membership still decides — and a wrong ID gets the same answer as an unknown one.
 */
function WorkspaceSignIn({ signedIn, onContinue, configError }: {
  signedIn: boolean;
  onContinue: () => Promise<void>;
  configError: string | null;
}) {
  const [workspaceId, setWorkspaceId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!looksLikeTenantId(workspaceId)) {
      setError('A workspace ID looks like 1b9d6bcd-bbfd-4b2d-9b5d-ab8dfbbd4bed. Ask your workspace administrator for it.');
      return;
    }
    setPendingTenantEntry(workspaceId);
    setBusy(true);
    try {
      if (signedIn) window.location.replace('/');
      else await onContinue();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
      setBusy(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-6">
      <form onSubmit={(e) => void submit(e)} className="w-full max-w-sm space-y-4 p-6 rounded-xl border border-border bg-card">
        <h1 className="text-xl font-semibold text-foreground">Sign in to a workspace</h1>
        <p className="text-sm text-muted-foreground">Enter the workspace ID your administrator gave you. You can also use the workspace&apos;s sign-in link.</p>
        {configError && <p className="text-warning text-sm">{configError}</p>}
        {error && <p role="alert" className="text-destructive text-sm">{error}</p>}
        <div>
          <label htmlFor="workspace-id" className="text-sm text-muted-foreground">Workspace ID</label>
          <input id="workspace-id" required autoFocus autoComplete="off" spellCheck={false} inputMode="text"
            value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)}
            className="mt-1.5 w-full px-3 py-2 rounded-md border border-border bg-input text-sm font-mono" />
        </div>
        <button type="submit" disabled={busy}
          className="w-full px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
          {busy ? 'Continuing…' : 'Continue'}
        </button>
        <a href="/" className="block text-center text-sm text-muted-foreground underline">Sign in without a workspace ID</a>
      </form>
    </div>
  );
}

function GateSpinner({ label }: { label: string }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export function AuthGate() {
  const { user, loading, signIn, verifyMfa, mfaPending, configError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A redeemed Entra session must not stay on this route. The gate used to render
  // "Redirecting to sign-in…" even after Microsoft had already returned a token.
  useEffect(() => {
    if (ACTIVE_AUTH_PROVIDER !== 'entra' || loading || !user) return;
    const path = window.location.pathname;
    if (path === WORKSPACE_SIGN_IN_PATH) return;
    if (path === '/login' || path.startsWith('/login/')) window.location.replace('/');
  }, [user, loading]);

  if (ACTIVE_AUTH_PROVIDER === 'entra' && typeof window !== 'undefined' && window.location.pathname === WORKSPACE_SIGN_IN_PATH) {
    if (loading) return <GateSpinner label="Loading…" />;
    return <WorkspaceSignIn signedIn={!!user} configError={configError} onContinue={() => signIn('', '')} />;
  }

  // SaaS (Entra) bounces to Microsoft's managed sign-in via the provider; dev auto-authenticates.
  if (ACTIVE_AUTH_PROVIDER === 'entra' || ACTIVE_AUTH_PROVIDER === 'dev') {
    return <GateSpinner label={configError ?? (user ? 'Signing you in…' : 'Redirecting to sign-in…')} />;
  }

  // Inferrex-JWT (sovereign / enterprise) — tenant-owned email/password + MFA challenge.
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mfaPending) await verifyMfa(code);
      else await signIn(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <form onSubmit={(e) => void submit(e)} className="w-full max-w-sm space-y-4 p-6 rounded-xl border border-border bg-card">
        <h1 className="text-xl font-semibold text-foreground">Sign in</h1>
        {configError && <p className="text-warning text-sm">{configError}</p>}
        {error && <p className="text-destructive text-sm">{error}</p>}
        {mfaPending ? (
          <div>
            <label htmlFor="mfa" className="text-sm text-muted-foreground">Authentication code</label>
            <input id="mfa" inputMode="numeric" autoComplete="one-time-code" required autoFocus
              value={code} onChange={(e) => setCode(e.target.value)}
              className="mt-1.5 w-full px-3 py-2 rounded-md border border-border bg-input text-sm" />
          </div>
        ) : (
          <>
            <div>
              <label htmlFor="email" className="text-sm text-muted-foreground">Email</label>
              <input id="email" type="email" required autoFocus autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-border bg-input text-sm" />
            </div>
            <div>
              <label htmlFor="password" className="text-sm text-muted-foreground">Password</label>
              <input id="password" type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full px-3 py-2 rounded-md border border-border bg-input text-sm" />
            </div>
          </>
        )}
        <button type="submit" disabled={busy}
          className="w-full px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50">
          {busy ? 'Signing in…' : mfaPending ? 'Verify' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
